import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Grid, GripVertical, MapPin, Plus, Settings, Trash2, Users } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import type { PlaybookWithCount } from '@/db/Types'
import type { DocumentTemplateType, CreateDocumentSectionRequest } from '@/types/blocks'
import { useSettings } from '@/contexts/SettingsContext'
import { DOCUMENT_TEMPLATES, type DocumentTemplate, SECTION_COLORS } from '@/constants/DocumentTemplates'
import { calculateGridPosition } from '@/utils/GridLayout.utils'
import './new-document-dialog.css'

// Default section size in points - sections can be resized but start at this size
const DEFAULT_SECTION_SIZE = { width: 200, height: 300 }

// ========== Types ==========

type Step = 'basic' | 'template' | 'sections'

interface SectionConfig {
	id: string // Temp ID for React keys
	name: string
	color: string
	filterPlayType: 'run' | 'pass' | null
}

type NewDocumentDialogProps = {
	isOpen: boolean
	onClose: () => void
	onCreateDocument: (
		playbookId: string,
		name: string,
		templateType: DocumentTemplateType,
		sections: CreateDocumentSectionRequest[],
		description?: string,
	) => Promise<void>
	playbooks: PlaybookWithCount[]
}

// ========== Step Indicators ==========

const STEPS: { id: Step; label: string }[] = [
	{ id: 'basic', label: 'Basic Info' },
	{ id: 'template', label: 'Template' },
	{ id: 'sections', label: 'Sections' },
]

function StepIndicator({ currentStep }: { currentStep: Step }) {
	const currentIndex = STEPS.findIndex(s => s.id === currentStep)

	return (
		<div className="document-dialog-steps">
			{STEPS.map((step, index) => {
				const isComplete = index < currentIndex
				const isCurrent = step.id === currentStep

				return (
					<div
						key={step.id}
						className="document-dialog-step"
						data-active={isCurrent}
						data-complete={isComplete}
					>
						<div className="document-dialog-step-number">
							{isComplete ? '✓' : index + 1}
						</div>
						<span className="document-dialog-step-label">{step.label}</span>
					</div>
				)
			})}
		</div>
	)
}

// ========== Template Card ==========

function TemplateCard({
						  template,
						  isSelected,
						  onClick,
					  }: {
	template: DocumentTemplate
	isSelected: boolean
	onClick: () => void
}) {
	const IconComponent = {
		grid: Grid,
		'map-pin': MapPin,
		users: Users,
		settings: Settings,
	}[template.icon]

	return (
		<button
			type="button"
			className="document-template-card"
			data-selected={isSelected}
			onClick={onClick}
		>
			<div className="document-template-icon">
				<IconComponent size={24} />
			</div>
			<div className="document-template-info">
				<h4 className="document-template-name">{template.name}</h4>
				<p className="document-template-description">{template.description}</p>
			</div>
			{template.sections.length > 0 && (
				<div className="document-template-preview">
					{template.sections.slice(0, 4).map((section, i) => (
						<div
							key={i}
							className="document-template-section-dot"
							style={{ backgroundColor: section.color }}
						/>
					))}
					{template.sections.length > 4 && (
						<span className="document-template-more">
							+{template.sections.length - 4}
						</span>
					)}
				</div>
			)}
		</button>
	)
}

// ========== Section Editor ==========

interface DragState {
	index: number           // Which section is being dragged
	startY: number          // Mouse Y when drag started
	currentY: number        // Current mouse Y position
	rowHeight: number       // Height of a section row for calculations
}

function SectionEditor({
						   sections,
						   onSectionsChange,
					   }: {
	sections: SectionConfig[]
	onSectionsChange: (sections: SectionConfig[]) => void
}) {
	const [dragState, setDragState] = useState<DragState | null>(null)

	const handleAddSection = useCallback(() => {
		const colorKeys = Object.keys(SECTION_COLORS) as Array<keyof typeof SECTION_COLORS>
		const usedColors = new Set(sections.map(s => s.color))
		const availableColor =
			colorKeys.find(key => !usedColors.has(SECTION_COLORS[key])) ?? 'blue'

		onSectionsChange([
			...sections,
			{
				id: crypto.randomUUID(),
				name: `Section ${sections.length + 1}`,
				color: SECTION_COLORS[availableColor],
				filterPlayType: null,
			},
		])
	}, [sections, onSectionsChange])

	const handleRemoveSection = useCallback(
		(id: string) => {
			onSectionsChange(sections.filter(s => s.id !== id))
		},
		[sections, onSectionsChange],
	)

	const handleUpdateSection = useCallback(
		(id: string, updates: Partial<SectionConfig>) => {
			onSectionsChange(
				sections.map(s => (s.id === id ? { ...s, ...updates } : s)),
			)
		},
		[sections, onSectionsChange],
	)

	const handleMouseDown = useCallback((e: React.MouseEvent, index: number) => {
		e.preventDefault()
		const row = e.currentTarget.closest('.document-section-row') as HTMLElement
		const rowHeight = row?.offsetHeight ?? 0

		setDragState({
			index,
			startY: e.clientY,
			currentY: e.clientY,
			rowHeight,
		})
	}, [])

	const handleMouseMove = useCallback((e: MouseEvent) => {
		if (!dragState) return
		setDragState(prev => prev ? { ...prev, currentY: e.clientY } : null)
	}, [dragState])

	const handleMouseUp = useCallback(() => {
		if (!dragState) return

		// Calculate final drop position
		const deltaY = dragState.currentY - dragState.startY
		const moveBy = Math.round(deltaY / dragState.rowHeight)
		const newIndex = Math.max(0, Math.min(sections.length - 1, dragState.index + moveBy))

		if (newIndex !== dragState.index) {
			const newSections = [...sections]
			const [removed] = newSections.splice(dragState.index, 1)
			if (removed) {
				newSections.splice(newIndex, 0, removed)
				onSectionsChange(newSections)
			}
		}

		setDragState(null)
	}, [dragState, sections, onSectionsChange])

	// Add global mouse listeners when dragging
	useEffect(() => {
		if (dragState) {
			window.addEventListener('mousemove', handleMouseMove)
			window.addEventListener('mouseup', handleMouseUp)
			return () => {
				window.removeEventListener('mousemove', handleMouseMove)
				window.removeEventListener('mouseup', handleMouseUp)
			}
		}
	}, [dragState, handleMouseMove, handleMouseUp])

	// Prevent text selection during drag
	useEffect(() => {
		if (dragState) {
			document.body.style.userSelect = 'none'
			document.body.style.cursor = 'grabbing'
			return () => {
				document.body.style.userSelect = ''
				document.body.style.cursor = ''
			}
		}
	}, [dragState])

	// Calculate visual offsets for each row during drag
	const getRowStyle = useCallback((index: number): React.CSSProperties => {
		if (!dragState) return {}

		const { index: dragIndex, startY, currentY, rowHeight } = dragState
		const deltaY = currentY - startY

		if (index === dragIndex) {
			// Dragged row follows cursor
			return {
				transform: `translateY(${deltaY}px)`,
				zIndex: 10,
				boxShadow: 'var(--shadow-elevation-high)',
				transition: 'box-shadow 200ms',
			}
		}

		// Calculate if this row should shift
		const draggedCurrentPos = dragIndex + deltaY / rowHeight

		if (index > dragIndex && index <= draggedCurrentPos) {
			// Shift up
			return { transform: `translateY(-${rowHeight}px)`, transition: 'transform 200ms ease-out' }
		}
		if (index < dragIndex && index >= draggedCurrentPos) {
			// Shift down
			return { transform: `translateY(${rowHeight}px)`, transition: 'transform 200ms ease-out' }
		}

		return { transition: 'transform 200ms ease-out' }
	}, [dragState])

	return (
		<div className="document-sections-editor">
			<div className="document-sections-list">
				{sections.map((section, index) => (
					<div key={section.id} className="document-section-row" style={getRowStyle(index)}>
						<div
							className="document-section-drag"
							onMouseDown={(e) => handleMouseDown(e, index)}
							data-dragging={dragState?.index === index}
						>
							<GripVertical size={20} className="document-section-grip" />
						</div>

						<Select
							value={section.color}
							onValueChange={(color: string) =>
								handleUpdateSection(section.id, { color })
							}
						>
							<SelectTrigger className="document-section-color-trigger">
								<div
									className="document-section-color-swatch"
									style={{ backgroundColor: section.color }}
								/>
							</SelectTrigger>
							<SelectContent>
								{Object.entries(SECTION_COLORS).map(([name, hex]) => (
									<SelectItem key={hex} value={hex}>
										<div className="document-color-option">
											<div
												className="document-color-swatch"
												style={{ backgroundColor: hex }}
											/>
											<span>{name}</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<Input
							type="text"
							value={section.name}
							onChange={e =>
								handleUpdateSection(section.id, { name: e.target.value })
							}
							className="document-section-name-input"
							placeholder="Section name"
						/>

						<Select
							value={section.filterPlayType ?? 'all'}
							onValueChange={value =>
								handleUpdateSection(section.id, {
									filterPlayType:
										value === 'all' ? null : (value as 'run' | 'pass'),
								})
							}
						>
							<SelectTrigger className="document-section-filter">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Plays</SelectItem>
								<SelectItem value="run">Runs Only</SelectItem>
								<SelectItem value="pass">Passes Only</SelectItem>
							</SelectContent>
						</Select>

						<button
							type="button"
							className="document-section-delete"
							onClick={() => handleRemoveSection(section.id)}
							title="Remove section"
						>
							<Trash2 size={16} />
						</button>
					</div>
				))}
			</div>

			<Button
				variant="ghost"
				className="document-add-section"
				onClick={handleAddSection}
			>
				<Plus size={16} />
				Add Section
			</Button>
		</div>
	)
}

// ========== Main Dialog ==========

export function NewDocumentDialog({
									   isOpen,
									   onClose,
									   onCreateDocument,
									   playbooks,
								   }: NewDocumentDialogProps) {
	const { lastViewedPlaybookId } = useSettings()

	// Step state
	const [currentStep, setCurrentStep] = useState<Step>('basic')

	// Form state
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [selectedPlaybookId, setSelectedPlaybookId] = useState<string | null>(null)
	const [selectedTemplateId, setSelectedTemplateId] =
		useState<DocumentTemplateType>('down_and_distance')
	const [sections, setSections] = useState<SectionConfig[]>([])
	const [isCreating, setIsCreating] = useState(false)

	// Derived state
	const sortedPlaybooks = useMemo(() => {
		return [...playbooks].sort((a, b) => {
			const dateA = new Date(a.updated_at).getTime()
			const dateB = new Date(b.updated_at).getTime()
			return dateB - dateA
		})
	}, [playbooks])

	const selectedPlaybook = useMemo(
		() => playbooks.find(p => p.id === selectedPlaybookId),
		[playbooks, selectedPlaybookId],
	)

	const selectedTemplate = useMemo(
		() => DOCUMENT_TEMPLATES.find(t => t.id === selectedTemplateId),
		[selectedTemplateId],
	)

	// Auto-select playbook and set default name when dialog opens
	useEffect(() => {
		const firstPlaybook = sortedPlaybooks[0]
		if (isOpen && firstPlaybook && !selectedPlaybookId) {
			const lastViewedExists =
				lastViewedPlaybookId &&
				sortedPlaybooks.some(p => p.id === lastViewedPlaybookId)

			const playbookToSelect = lastViewedExists
				? lastViewedPlaybookId
				: firstPlaybook.id

			setSelectedPlaybookId(playbookToSelect)

			// Set default name based on playbook
			const playbook = playbooks.find(p => p.id === playbookToSelect)
			if (playbook && !name) {
				setName(`${playbook.name} - Document`)
			}
		}
	}, [isOpen, sortedPlaybooks, selectedPlaybookId, lastViewedPlaybookId, playbooks, name])

	// Update default name when playbook changes
	useEffect(() => {
		if (selectedPlaybook && currentStep === 'basic') {
			const defaultName = `${selectedPlaybook.name} - Document`
			// Only update if name is empty or still a default format
			if (!name || name.endsWith(' - Document')) {
				setName(defaultName)
			}
		}
	}, [selectedPlaybook, currentStep, name])

	// Initialize sections when template changes or dialog opens
	useEffect(() => {
		const template = DOCUMENT_TEMPLATES.find(t => t.id === selectedTemplateId)
		if (template && isOpen) {
			setSections(
				template.sections.map(s => ({
					id: crypto.randomUUID(),
					name: s.name,
					color: s.color,
					filterPlayType: s.filterPlayType,
				})),
			)
		}
	}, [selectedTemplateId, isOpen])

	// Reset form
	const resetForm = useCallback(() => {
		setCurrentStep('basic')
		setName('')
		setDescription('')
		setSelectedPlaybookId(null)
		setSelectedTemplateId('down_and_distance')
		setSections([])
		setIsCreating(false)
	}, [])

	// Navigation
	const goToStep = useCallback((step: Step) => {
		setCurrentStep(step)
	}, [])

	const goNext = useCallback(() => {
		if (currentStep === 'basic') {
			goToStep('template')
		} else if (currentStep === 'template') {
			goToStep('sections')
		}
	}, [currentStep, goToStep])

	const goBack = useCallback(() => {
		if (currentStep === 'template') {
			goToStep('basic')
		} else if (currentStep === 'sections') {
			goToStep('template')
		}
	}, [currentStep, goToStep])

	// Validation
	const canProceed = useMemo(() => {
		if (currentStep === 'basic') {
			return name.trim().length > 0 && selectedPlaybookId !== null
		}
		if (currentStep === 'template') {
			return selectedTemplateId !== null
		}
		return currentStep === 'sections'

	}, [currentStep, name, selectedPlaybookId, selectedTemplateId])

	// Create handler
	const handleCreate = useCallback(async () => {
		if (!selectedPlaybookId || !name.trim()) return

		setIsCreating(true)
		try {
			const sectionRequests: CreateDocumentSectionRequest[] = sections.map(
				(s, index) => {
					const position = calculateGridPosition(index, DEFAULT_SECTION_SIZE)
					return {
						name: s.name,
						display_order: index,
						color: s.color,
						filter_play_type: s.filterPlayType ?? undefined,
						position_x: position.x,
						position_y: position.y,
						width: DEFAULT_SECTION_SIZE.width,
						height: DEFAULT_SECTION_SIZE.height,
					}
				},
			)

			await onCreateDocument(
				selectedPlaybookId,
				name.trim(),
				selectedTemplateId,
				sectionRequests,
				description.trim() || undefined,
			)
			resetForm()
			onClose()
		} catch (error) {
			console.error('Failed to create document:', error)
		} finally {
			setIsCreating(false)
		}
	}, [
		selectedPlaybookId,
		name,
		selectedTemplateId,
		sections,
		description,
		onCreateDocument,
		resetForm,
		onClose,
	])

	// Keyboard handler
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault()
				if (currentStep === 'sections') {
					handleCreate()
				} else if (canProceed) {
					goNext()
				}
			}
		},
		[currentStep, canProceed, goNext, handleCreate],
	)

	// Dialog close handler
	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				resetForm()
				onClose()
			}
		},
		[resetForm, onClose],
	)

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="document-dialog-content">
				<DialogHeader>
					<DialogTitle>Create New Document</DialogTitle>
				</DialogHeader>

				<StepIndicator currentStep={currentStep} />

				<div className="document-dialog-body" onKeyDown={handleKeyDown}>
					{/* Step 1: Basic Info */}
					<div className="document-step-wrapper" data-active={currentStep === 'basic'}>
						<div className="document-step-content">
							<div className="form-field">
								<label className="form-label">Source Playbook</label>
								<Select
									value={selectedPlaybookId ?? ''}
									onValueChange={value =>
										setSelectedPlaybookId(value)
									}
									disabled={sortedPlaybooks.length === 0}
								>
									<SelectTrigger>
										<SelectValue
											placeholder={
												sortedPlaybooks.length === 0
													? 'No playbooks available'
													: 'Select a playbook'
											}
										/>
									</SelectTrigger>
									<SelectContent>
										{sortedPlaybooks.map(playbook => (
											<SelectItem
												key={playbook.id}
												value={playbook.id.toString()}
											>
												{playbook.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="form-field">
								<label className="form-label">Document Name</label>
								<Input
									type="text"
									value={name}
									onChange={e => setName(e.target.value)}
									placeholder="Enter document name..."
									autoFocus
									disabled={sortedPlaybooks.length === 0}
								/>
							</div>

							<div className="form-field">
								<label className="form-label">Description (Optional)</label>
								<Input
									type="text"
									value={description}
									onChange={e => setDescription(e.target.value)}
									placeholder="e.g., Week 3 vs Tigers"
									disabled={sortedPlaybooks.length === 0}
								/>
							</div>

							{sortedPlaybooks.length === 0 && (
								<p className="dialog-warning">
									You need to create a playbook first before creating a
									document.
								</p>
							)}
						</div>
					</div>

					{/* Step 2: Template Selection */}
					<div className="document-step-wrapper" data-active={currentStep === 'template'}>
						<div className="document-step-content">
							<p className="document-step-description">
								Choose a template to organize your document. You can
								customize sections in the next step.
							</p>
							<div className="document-templates-grid">
								{DOCUMENT_TEMPLATES.map(template => (
									<TemplateCard
										key={template.id}
										template={template}
										isSelected={selectedTemplateId === template.id}
										onClick={() => setSelectedTemplateId(template.id)}
									/>
								))}
							</div>
						</div>
					</div>

					{/* Step 3: Section Configuration */}
					<div className="document-step-wrapper" data-active={currentStep === 'sections'}>
						<div className="document-step-content">
							<p className="document-step-description">
								Configure your sections. You can add, remove, or reorder them.
								Plays will be organized into these sections.
							</p>
							<SectionEditor
								sections={sections}
								onSectionsChange={setSections}
							/>
						</div>
					</div>
				</div>

				{/* Navigation */}
				<div className="document-dialog-actions">
					{currentStep !== 'basic' ? (
						<Button
							variant="ghost"
							onClick={goBack}
							disabled={isCreating}
						>
							<ChevronLeft size={16} />
							Back
						</Button>
					) : (
						<Button
							variant="ghost"
							onClick={onClose}
							disabled={isCreating}
						>
							Cancel
						</Button>
					)}

					{currentStep === 'sections' ? (
						<Button
							variant="action"
							onClick={handleCreate}
							disabled={!canProceed || isCreating}
						>
							{isCreating ? 'Creating...' : 'Create Document'}
						</Button>
					) : (
						<Button
							variant="action"
							onClick={goNext}
							disabled={!canProceed}
						>
							Next
							<ChevronRight size={16} />
						</Button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
